/**
 * Meeting Modules Selectors
 * Memoized selectors for efficient data access
 */

import { createSelector } from "@reduxjs/toolkit";
import { RootState } from "@/store";

// Base selectors
export const selectMeetingModulesState = (state: RootState) =>
  state.meetingModules;

// Memoized selectors
export const selectAllCategories = createSelector(
  [selectMeetingModulesState],
  (state) => state?.categories || []
);

export const selectCategoriesLoading = createSelector(
  [selectMeetingModulesState],
  (state) => state?.loading || false
);

export const selectCategoriesError = createSelector(
  [selectMeetingModulesState],
  (state) => state?.error || null
);

export const selectSelectedCategoryId = createSelector(
  [selectMeetingModulesState],
  (state) => state?.selectedCategoryId || null
);

export const selectSelectedModuleId = createSelector(
  [selectMeetingModulesState],
  (state) => state?.selectedModuleId || null
);

// Get selected category
export const selectSelectedCategory = createSelector(
  [selectAllCategories, selectSelectedCategoryId],
  (categories, selectedId) =>
    selectedId ? categories.find((cat) => cat.id === selectedId) : null
);

// Get selected module
export const selectSelectedModule = createSelector(
  [selectAllCategories, selectSelectedCategoryId, selectSelectedModuleId],
  (categories, categoryId, moduleId) => {
    if (!categoryId || !moduleId) return null;
    const category = categories.find((cat) => cat.id === categoryId);
    return category?.modules.find((mod) => mod.id === moduleId) || null;
  }
);

// Check if cache is fresh (< 5 minutes old)
export const selectIsCacheFresh = createSelector(
  [selectMeetingModulesState],
  (state) => {
    if (!state || !state.lastFetched) return false;
    const cacheAge = Date.now() - state.lastFetched;
    return cacheAge < 5 * 60 * 1000; // 5 minutes
  }
);

// Get modules grouped by CEFR level for a category
export const selectModulesByLevel = createSelector(
  [selectSelectedCategory],
  (category) => {
    if (!category) return {};

    return category.modules.reduce(
      (acc, module) => {
        const level = module.level.cefr;
        if (!acc[level]) {
          acc[level] = [];
        }
        acc[level].push(module);
        return acc;
      },
      {} as Record<string, typeof category.modules>
    );
  }
);

// Get total module count
export const selectTotalModulesCount = createSelector(
  [selectAllCategories],
  (categories) => {
    if (!categories || !Array.isArray(categories)) return 0;
    return categories.reduce((sum, cat) => sum + cat.modules.length, 0);
  }
);

// Get total module count rounded down to nearest 10 (e.g., 57 -> 50)
export const selectTotalModulesCountRounded = createSelector(
  [selectTotalModulesCount],
  (count) => {
    if (count === 0) return 0;
    return Math.floor(count / 10) * 10;
  }
);

// Find a module by ID across all categories
export const selectModuleById = (moduleId: string) =>
  createSelector([selectAllCategories], (categories) => {
    for (const category of categories) {
      const module = category.modules.find((mod) => mod.id === moduleId);
      if (module) {
        return {
          module,
          category,
        };
      }
    }
    return null;
  });
